import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import SmoothScroll from "./components/SmoothScroll";
import Nav from "./components/Nav";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import About from "./pages/About";
import ProductPage from "./pages/ProductPage";
import Sustainable from "./pages/Sustainable";
import Industries from "./pages/Industries";
import CustomPrinting from "./pages/CustomPrinting";
import ExportPage from "./pages/Export";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import AuthCallback from "./pages/AuthCallback";
import LocationPage from "./pages/LocationPage";
import NotFound from "./pages/NotFound";
import SeoLandingPage from "./pages/SeoLandingPage";
import StickyCTA from "./components/StickyCTA";
import Analytics from "./components/Analytics";

import "./index.css";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
    return null;
}

function LegacyRedirect() {
    const location = useLocation();
    const map = { "/products/soup-bowls": "/products/paper-soup-bowls", "/products/pet-lids": "/products/pet-flat-lids", "/products/paper-containers": "/products/paper-salad-bowls", "/products/salad-bowls": "/products/paper-salad-bowls" };
    const target = map[location.pathname] || "/";
    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex,nofollow" />
                <link rel="canonical" href={`https://thepuropack.com${target}`} />
            </Helmet>
            <Navigate to={target} replace />
        </>
    );
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/products/:slug" element={<ProductPage />} />
            <Route path="/products/soup-bowls" element={<LegacyRedirect />} />
            <Route path="/products/salad-bowls" element={<LegacyRedirect />} />
            <Route path="/products/pet-lids" element={<LegacyRedirect />} />
            <Route path="/products/paper-containers" element={<LegacyRedirect />} />
            <Route path="/sustainable-packaging" element={<Sustainable />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/custom-printing" element={<CustomPrinting />} />
            <Route path="/export" element={<ExportPage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/paper-bowl-manufacturer-bangalore" element={<LocationPage />} />
            <Route path="/paper-bowl-manufacturer-india" element={<LocationPage />} />
            <Route path="/paper-food-packaging-india" element={<LocationPage />} />
            <Route path="/sustainable-packaging-india" element={<LocationPage />} />
            <Route path="/salad-bowl-manufacturer-india" element={<LocationPage />} />
            <Route path="/paper-salad-bowls-manufacturer-india" element={<SeoLandingPage />} />
            <Route path="/soup-bowl-manufacturer-bangalore" element={<SeoLandingPage />} />
            <Route path="/paper-food-containers-manufacturer" element={<SeoLandingPage />} />
            <Route path="/kraft-paper-bowls" element={<SeoLandingPage />} />
            <Route path="/white-paper-salad-bowls" element={<SeoLandingPage />} />
            <Route path="/custom-printed-paper-bowls" element={<SeoLandingPage />} />
            <Route path="/food-packaging-exporter-india" element={<SeoLandingPage />} />
            <Route path="/sustainable-food-packaging" element={<SeoLandingPage />} />
            <Route path="/paper-bowl-supplier-bangalore" element={<SeoLandingPage />} />
            <Route path="/pet-flat-lids-paper-bowls" element={<SeoLandingPage />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <SmoothScroll>
                        <div className="noise min-h-screen bg-bone text-inkgreen font-sans">
                            <Analytics />
                            <ScrollToTop />
                            <Nav />
                            <main><AppRouter /></main>
                            <Footer />
                            <StickyCTA />
                            <Toaster position="bottom-right" richColors />
                        </div>
                    </SmoothScroll>
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}
